import React, { useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import PingGrid from "../components/PingGrid";
import Feed from "../components/Feed";
import StreakStrip from "../components/StreakStrip";
import { supabase } from "../lib/supabaseClient";
import { fetchLatestPings } from "../lib/pings";
import { getMyProfile } from "../lib/profile";
import { ui, cx } from "../ui/ui";

function fmtWhen(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function kindNice(kind) {
  return String(kind || "").replaceAll("_", " ");
}

export default function Home() {
  const [meId, setMeId] = useState("");

  const [myLabel, setMyLabel] = useState("Me");
  const [partnerLabel, setPartnerLabel] = useState("Partner");

  const [lastPing, setLastPing] = useState(null);
  const [loadingLast, setLoadingLast] = useState(true);

  async function loadMe() {
    const { data } = await supabase.auth.getUser();
    setMeId(data?.user?.id || "");
  }

  async function loadLabels() {
    const { data, error } = await getMyProfile();
    if (error) return;
    if (data?.my_label) setMyLabel(data.my_label);
    if (data?.partner_label) setPartnerLabel(data.partner_label);
  }

  async function loadLast() {
    setLoadingLast(true);
    const { data, error } = await fetchLatestPings(1);
    setLoadingLast(false);
    if (error) return;
    setLastPing((data && data[0]) || null);
  }

  useEffect(() => {
    loadMe();
    loadLabels();
    loadLast();
  }, []);

  const lastSender = useMemo(() => {
    if (!lastPing) return "";
    const mine = meId && lastPing.sender_id === meId;
    return mine ? (myLabel || "Me") : (partnerLabel || "Partner");
  }, [lastPing, meId, myLabel, partnerLabel]);

  return (
    <div className="grid gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xl font-black tracking-tight">Home</div>
          <div className="text-sm text-white/70">One-tap pings. Low effort, high signal.</div>
        </div>
        <span className={ui.pill}>MVP</span>
      </div>

      <Card title="Streaks" subtitle="Keep the connection warm, not perfect.">
        <StreakStrip />
      </Card>

      <Card title="Send a ping" subtitle="Pick one, optionally add a note.">
        <PingGrid onSent={loadLast} />
      </Card>

      <Card
        title="Last ping"
        subtitle={lastPing ? fmtWhen(lastPing.created_at) : "Nothing yet"}
        right={
          <button className={cx(ui.btn, ui.btnGhost, "rounded-full px-3 py-1 text-xs")} onClick={loadLast}>
            Refresh
          </button>
        }
      >
        {loadingLast ? (
          <div className="text-sm text-white/70">Loading…</div>
        ) : !lastPing ? (
          <div className="text-sm text-white/70">Send your first ping above 🦦</div>
        ) : (
          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-black">{lastSender}</div>
              <div className="rounded-full border border-line2 bg-black/10 px-3 py-1 text-xs font-black text-white/80">
                {kindNice(lastPing.kind)}
              </div>
            </div>
            {lastPing.note ? (
              <div className="text-sm text-white/85">{lastPing.note}</div>
            ) : (
              <div className="text-sm text-white/60">No note</div>
            )}
          </div>
        )}
      </Card>

      <Card title="Recent" subtitle="Latest 5 pings.">
        <Feed meId={meId} myLabel={myLabel} partnerLabel={partnerLabel} limit={5} title="Feed preview" compact />
      </Card>

      <Card title="Timeline" subtitle="Latest 100 pings grouped by day.">
        <Feed meId={meId} myLabel={myLabel} partnerLabel={partnerLabel} limit={100} title="Timeline" />
      </Card>
    </div>
  );
}
